<div>
  <div class="container-fluid">
    <!-- Back Button -->
    <div class="mb-3">
      <button type="button" class="btn btn-outline-secondary" (click)="goBack()">
        <i class="bi bi-arrow-left me-2"></i>
        Back to Drone Catalog
      </button>
    </div>

    <!-- Loading State -->
    <div *ngIf="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <!-- Error Message -->
    <div *ngIf="errorMessage" class="alert alert-danger alert-dismissible fade show" role="alert">
      <i class="bi bi-exclamation-triangle-fill me-2"></i>
      {{ errorMessage }}
      <button type="button" class="btn-close" aria-label="Close" (click)="errorMessage = ''" aria-label="Close"></button>
    </div>

    <!-- Manufacturer Details -->
    <div *ngIf="!loading && manufacturer" class="row">
      <!-- Left Column - Main Info -->
      <div class="col-lg-8 mb-4">
        <!-- Manufacturer Header Card -->
        <div class="card">
          <div class="card-header">
            <div class="d-flex justify-content-between align-items-center">
              <div class="d-flex align-items-center">
                <div class="entity-avatar entity-avatar-lg entity-avatar-square fs-4 me-3">
                  <i class="bi bi-cpu"></i>
                </div>
                <div>
                  <h5 class="mb-0">{{ manufacturer.name }}</h5>
                  <span class="text-muted" *ngIf="models.length > 0">{{ models.length }} model{{ models.length !== 1 ? 's' : '' }}</span>
                </div>
              </div>
              <div *ngIf="manufacturer.url">
                <button type="button" class="btn btn-outline-primary btn-sm" (click)="openWebsite()">
                  <i class="bi bi-box-arrow-up-right me-2"></i>
                  Visit Website
                </button>
              </div>
            </div>
          </div>
          <div class="card-body" *ngIf="manufacturer.description">
            <h6 class="small fw-semibold text-uppercase text-body-secondary mb-3">
              <i class="bi bi-info-circle text-primary me-2"></i>
              Description
            </h6>
            <p class="mb-0">{{ manufacturer.description }}</p>
          </div>
        </div>

        <!-- Models Card -->
        <div class="card mt-4">
          <div class="card-header">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <h5 class="mb-0">
                  <i class="bi bi-grid me-2"></i>
                  Drone Models
                </h5>
              </div>
              <div class="d-flex align-items-center gap-2">
                <span class="badge bg-secondary">{{ models.length }}</span>
                <button type="button" class="btn btn-primary btn-sm" (click)="addModel()">
                  <i class="bi bi-plus-lg me-1"></i>
                  Add Model
                </button>
              </div>
            </div>
          </div>
          <div class="card-body p-0">
            <!-- Loading Models -->
            <div *ngIf="loadingModels" class="text-center py-4">
              <div class="spinner-border spinner-border-sm text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              <span class="ms-2 text-muted">Loading models...</span>
            </div>

            <!-- Models Table -->
            <div *ngIf="!loadingModels && models.length > 0" class="table-responsive">
              <table class="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>Model Name</th>
                    <th>Max Takeoff Weight</th>
                    <th>FAA Class</th>
                    <th>Description</th>
                    <th class="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let model of models">
                    <td>
                      <div class="d-flex align-items-center">
                        <div class="entity-avatar me-2">
                          <i class="adn-icon adn-icon-drone-solid"></i>
                        </div>
                        <span class="fw-medium">{{ model.modelName }}</span>
                      </div>
                    </td>
                    <td>
                      <span *ngIf="model.maxTakeoffWeightLbs !== null">
                        {{ formatWeight(model.maxTakeoffWeightLbs) }}
                      </span>
                      <span *ngIf="model.maxTakeoffWeightLbs === null" class="text-muted">-</span>
                    </td>
                    <td>
                      <span
                        class="badge"
                        [ngClass]="isOver55Lbs(model.maxTakeoffWeightLbs) ? 'bg-warning text-dark' : 'bg-success'"
                      >
                        {{ isOver55Lbs(model.maxTakeoffWeightLbs) ? 'Over 55 lbs' : 'Under 55 lbs' }}
                      </span>
                    </td>
                    <td>
                      <span *ngIf="model.description" class="text-truncate-cell d-block text-truncate">{{ model.description }}</span>
                      <span *ngIf="!model.description" class="text-muted">-</span>
                    </td>
                    <td class="text-end">
                      <button
                        type="button"
                        class="btn btn-sm btn-outline-primary me-1"
                        (click)="editModel(model)"
                        title="Edit model"
                      >
                        <i class="bi bi-pencil"></i>
                      </button>
                      <button
                        type="button"
                        class="btn btn-sm btn-outline-danger"
                        (click)="confirmArchiveModel(model)"
                        [disabled]="archivingModelId === model.id"
                        title="Archive model"
                      >
                        <i class="bi bi-archive" *ngIf="archivingModelId !== model.id"></i>
                        <span class="spinner-border spinner-border-sm" *ngIf="archivingModelId === model.id"></span>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- No Models -->
            <div *ngIf="!loadingModels && models.length === 0" class="text-center py-4 text-muted">
              <i class="bi bi-grid fs-1 d-block mb-2"></i>
              No drone models found for this manufacturer
            </div>
          </div>
        </div>
      </div>

      <!-- Right Column - Sidebar Info -->
      <div class="col-lg-4">
        <!-- Manufacturer Details -->
        <div class="card mb-4">
          <div class="card-header">
            <h5 class="mb-0">
              <i class="bi bi-info-circle me-2"></i>
              Details
            </h5>
          </div>
          <div class="card-body">
            <div class="py-2">
              <label class="d-block small fw-medium text-uppercase text-body-secondary mb-1">Manufacturer Name</label>
              <div class="fw-medium">{{ manufacturer.name }}</div>
            </div>
            <div class="py-2 border-top" *ngIf="manufacturer.url">
              <label class="d-block small fw-medium text-uppercase text-body-secondary mb-1">Website</label>
              <div>
                <a [href]="manufacturer.url" target="_blank" rel="noopener" class="link-underline-opacity-0 link-underline-opacity-100-hover">
                  {{ manufacturer.url }}
                  <i class="bi bi-box-arrow-up-right ms-1"></i>
                </a>
              </div>
            </div>
            <div class="py-2 border-top">
              <label class="d-block small fw-medium text-uppercase text-body-secondary mb-1">Created</label>
              <div>{{ formatDate(manufacturer.createdAt) }}</div>
            </div>
            <div class="py-2 border-top">
              <label class="d-block small fw-medium text-uppercase text-body-secondary mb-1">Last Updated</label>
              <div>{{ formatDate(manufacturer.updatedAt) }}</div>
            </div>
          </div>
        </div>

        <!-- Statistics -->
        <div class="card mb-4">
          <div class="card-header">
            <h5 class="mb-0">
              <i class="bi bi-bar-chart me-2"></i>
              Statistics
            </h5>
          </div>
          <div class="card-body">
            <div class="py-3 text-center">
              <div class="stat-value fw-bold text-primary">{{ models.length }}</div>
              <div class="small text-body-secondary">Total Models</div>
            </div>
            <div class="py-3 text-center border-top" *ngIf="models.length > 0">
              <div class="stat-value fw-bold text-primary">{{ modelsOver55LbsCount }}</div>
              <div class="small text-body-secondary">Models Over 55 lbs</div>
            </div>
          </div>
        </div>

        <!-- Actions -->
        <div class="card">
          <div class="card-header">
            <h5 class="mb-0">
              <i class="bi bi-gear me-2"></i>
              Actions
            </h5>
          </div>
          <div class="card-body">
            <div class="d-grid gap-2">
              <button type="button" class="btn btn-primary" (click)="editManufacturer()">
                <i class="bi bi-pencil me-2"></i>
                Edit Manufacturer
              </button>
              <button
                type="button"
                class="btn btn-outline-danger"
                (click)="confirmArchive()"
                [disabled]="archiving"
              >
                <span *ngIf="archiving" class="spinner-border spinner-border-sm me-2"></span>
                <i *ngIf="!archiving" class="bi bi-archive me-2"></i>
                {{ archiving ? 'Archiving...' : 'Archive Manufacturer' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</div>

<!-- Archive Manufacturer Confirmation Modal -->
<div class="modal fade" id="archiveModal" tabindex="-1" aria-labelledby="archiveModalLabel" aria-hidden="true">
  <div class="modal-dialog modal-dialog-centered">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title" id="archiveModalLabel">
          <i class="bi bi-exclamation-triangle text-warning me-2"></i>
          Archive Manufacturer
        </h5>
        <button type="button" class="btn-close" aria-label="Close" data-bs-dismiss="modal" aria-label="Close"></button>
      </div>
      <div class="modal-body">
        <p>Are you sure you want to archive <strong>{{ manufacturer?.name }}</strong>?</p>
        <p class="text-muted mb-0">
          This will hide the manufacturer from the catalog. The manufacturer can be restored later if needed.
        </p>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
        <button type="button" class="btn btn-danger" (click)="archiveManufacturer()" [disabled]="archiving">
          <span *ngIf="archiving" class="spinner-border spinner-border-sm me-2"></span>
          <i *ngIf="!archiving" class="bi bi-archive me-2"></i>
          {{ archiving ? 'Archiving...' : 'Archive' }}
        </button>
      </div>
    </div>
  </div>
</div>

<!-- Archive Model Confirmation Modal -->
<div class="modal fade" id="archiveModelModal" tabindex="-1" aria-labelledby="archiveModelModalLabel" aria-hidden="true">
  <div class="modal-dialog modal-dialog-centered">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title" id="archiveModelModalLabel">
          <i class="bi bi-exclamation-triangle text-warning me-2"></i>
          Archive Drone Model
        </h5>
        <button type="button" class="btn-close" aria-label="Close" data-bs-dismiss="modal" aria-label="Close"></button>
      </div>
      <div class="modal-body">
        <p>Are you sure you want to archive <strong>{{ modelToArchive?.modelName }}</strong>?</p>
        <p class="text-muted mb-0">
          This will hide the model from the catalog. The model can be restored later if needed.
        </p>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
        <button type="button" class="btn btn-danger" (click)="archiveModel()" [disabled]="archivingModelId">
          <span *ngIf="archivingModelId" class="spinner-border spinner-border-sm me-2"></span>
          <i *ngIf="!archivingModelId" class="bi bi-archive me-2"></i>
          {{ archivingModelId ? 'Archiving...' : 'Archive' }}
        </button>
      </div>
    </div>
  </div>
</div>
